<template>
  <main class="pixso-page tunnel-solution" :class="{ 'tunnel-solution--en': isEnglish }">
    <section class="tunnel-solution__hero pixso-bleed" aria-labelledby="tunnel-hero-title">
      <video
        class="tunnel-solution__hero-video"
        autoplay
        muted
        loop
        playsinline
        aria-hidden="true"
      >
        <source :src="airGroundBannerVideo" type="video/mp4" />
      </video>
      <div class="tunnel-solution__hero-copy">
        <h1 id="tunnel-hero-title" class="tunnel-solution__hero-title">
          {{ t('TunnelCorridorInspection.hero.title') }}
        </h1>
        <p class="tunnel-solution__hero-kicker">{{ t('TunnelCorridorInspection.hero.kicker') }}</p>
      </div>
    </section>

    <section class="tunnel-solution__section tunnel-solution__pain">
      <SectionHeader
        :eyebrow="t('TunnelCorridorInspection.sections.pain.eyebrow')"
        :accent="t('TunnelCorridorInspection.sections.pain.titleAccent')"
        :title="t('TunnelCorridorInspection.sections.pain.title')"
      />

      <div class="pixso-container tunnel-solution__pain-shell">
        <AccordionImageGallery
          :items="painGalleryItems"
          :aria-label="t('TunnelCorridorInspection.sections.pain.eyebrow')"
        />
      </div>
    </section>

    <section
      ref="howSectionRef"
      class="tunnel-solution__how pixso-bleed"
    >
      <div class="tunnel-solution__how-stage">
        <SectionHeader
          :eyebrow="t('TunnelCorridorInspection.sections.how.eyebrow')"
          :accent="t('TunnelCorridorInspection.sections.how.titleAccent')"
          :title="t('TunnelCorridorInspection.sections.how.title')"
        />

        <div class="tunnel-solution__how-card-shell">
          <figure
            ref="howVideoCardRef"
            class="tunnel-solution__how-video-card"
            data-text-reveal="off"
            :aria-hidden="isHowVideoInteractive ? undefined : 'true'"
          >
            <video
              ref="howVideoRef"
              :key="isEnglish ? 'en' : 'zh'"
              class="tunnel-solution__how-video"
              :poster="isEnglish ? tunnelHowVideoPosterEn : tunnelHowVideoPosterZh"
              controls
              preload="metadata"
              playsinline
              :aria-label="t('TunnelCorridorInspection.sections.how.playLabel')"
              @play="showHowVideoOverlay = false"
            >
              <source :src="isEnglish ? tunnelTetherVideoEn : tunnelTetherVideo" type="video/mp4" />
            </video>
            <button
              v-if="showHowVideoOverlay"
              class="tunnel-solution__how-video-overlay"
              type="button"
              :style="howVideoOverlayStyle"
              :aria-label="t('TunnelCorridorInspection.sections.how.playLabel')"
              @click="playHowVideo"
            />
          </figure>
        </div>
      </div>
    </section>

    <section class="tunnel-solution__section tunnel-solution__features">
      <SectionHeader
        :eyebrow="t('TunnelCorridorInspection.sections.features.eyebrow')"
        :accent="t('TunnelCorridorInspection.sections.features.titleAccent')"
        :title="featureTitle"
      />

      <div class="tunnel-solution__wide tunnel-solution__feature-grid" data-text-reveal="off">
        <div class="tunnel-solution__feature-top-row">
          <article class="tunnel-solution__feature-card tunnel-solution__feature-card--hero">
          <img :src="featureDogDroneImage" :alt="t('TunnelCorridorInspection.alt.featureMainImage')" loading="lazy" />
          <div class="tunnel-solution__feature-hero-copy">
            <h3>{{ t('TunnelCorridorInspection.features.robot.title') }}</h3>
            <p>{{ t('TunnelCorridorInspection.features.robot.subtitle') }}</p>
          </div>
          <div class="tunnel-solution__feature-size">
            <strong>≤113cm × 53cm × 125cm</strong>
            <span>{{ t('TunnelCorridorInspection.features.robot.dimensionsLabel') }}</span>
          </div>
          </article>

          <div class="tunnel-solution__feature-right-stack">
            <article class="tunnel-solution__feature-card tunnel-solution__feature-card--ai">
              <img :src="featureAiImage" :alt="t('TunnelCorridorInspection.features.ai.title')" loading="lazy" />
              <div class="tunnel-solution__feature-overlay tunnel-solution__feature-overlay--center">
                <h3>{{ t('TunnelCorridorInspection.features.ai.title') }}</h3>
                <p>{{ t('TunnelCorridorInspection.features.ai.line1') }}</p>
                <p>{{ t('TunnelCorridorInspection.features.ai.line2') }}</p>
              </div>
            </article>

            <div class="tunnel-solution__feature-mini-row">
              <article class="tunnel-solution__feature-card tunnel-solution__feature-card--slam">
                <img :src="featureSlamImage" :alt="t('TunnelCorridorInspection.features.slam.title')" loading="lazy" />
                <div class="tunnel-solution__feature-overlay">
                  <h3>{{ t('TunnelCorridorInspection.features.slam.title') }}</h3>
                  <p>{{ t('TunnelCorridorInspection.features.slam.description') }}</p>
                </div>
              </article>

              <article class="tunnel-solution__feature-card tunnel-solution__feature-card--copy">
                <div>
                  <h3>{{ t('TunnelCorridorInspection.features.semantic.title') }}</h3>
                  <p>{{ t('TunnelCorridorInspection.features.semantic.description') }}</p>
                </div>
                <img class="tunnel-solution__feature-icon-image" :src="featureSemanticIcon" alt="" aria-hidden="true" />
              </article>

              <article class="tunnel-solution__feature-card tunnel-solution__feature-card--copy">
                <div>
                  <h3>{{ t('TunnelCorridorInspection.features.sdk.title') }}</h3>
                  <p>{{ t('TunnelCorridorInspection.features.sdk.description') }}</p>
                </div>
                <img class="tunnel-solution__feature-icon-image" :src="featureSdkIcon" alt="" aria-hidden="true" />
              </article>
            </div>
          </div>
        </div>

        <div class="tunnel-solution__feature-bottom-row">
          <div class="tunnel-solution__feature-bottom-pair tunnel-solution__feature-bottom-pair--left">
            <article class="tunnel-solution__feature-card tunnel-solution__feature-card--metric-tall tunnel-solution__feature-card--duration">
              <div class="tunnel-solution__metric-copy">
                <strong>{{ t('TunnelCorridorInspection.summary.durationValue') }}</strong>
                <span>{{ t('TunnelCorridorInspection.summary.quadruped') }}</span>
                <span>{{ t('TunnelCorridorInspection.summary.uav') }}</span>
                <span>{{ t('TunnelCorridorInspection.summary.subtitle') }}</span>
              </div>
              <span class="tunnel-solution__battery-icon" aria-hidden="true">
                <img class="tunnel-solution__battery-icon-image" :src="chargeIcon" alt="" aria-hidden="true" />
              </span>
            </article>

            <div class="tunnel-solution__feature-column">
              <article class="tunnel-solution__feature-card tunnel-solution__feature-card--metric-wide">
                <div class="tunnel-solution__metric-copy">
                  <strong>{{ t('TunnelCorridorInspection.metrics.slope.value') }}</strong>
                  <span>{{ t('TunnelCorridorInspection.metrics.slope.title') }}</span>
                </div>
              </article>

              <article class="tunnel-solution__feature-card tunnel-solution__feature-card--metric-wide tunnel-solution__feature-card--image-wide tunnel-solution__feature-card--temperature">
                <img :src="featureTemperatureImage" :alt="t('TunnelCorridorInspection.metrics.temperature.title')" loading="lazy" />
                <div class="tunnel-solution__metric-copy">
                  <strong>{{ t('TunnelCorridorInspection.metrics.temperature.value') }}</strong>
                  <span>{{ t('TunnelCorridorInspection.metrics.temperature.title') }}</span>
                </div>
              </article>
            </div>
          </div>

          <div class="tunnel-solution__feature-bottom-pair tunnel-solution__feature-bottom-pair--right">
            <div class="tunnel-solution__feature-column">
              <article class="tunnel-solution__feature-card tunnel-solution__feature-card--metric-wide tunnel-solution__feature-card--image-wide tunnel-solution__feature-card--stairs">
                <img :src="featureStairsImage" :alt="t('TunnelCorridorInspection.metrics.stairs.title')" loading="lazy" />
                <div class="tunnel-solution__metric-copy">
                  <strong>{{ t('TunnelCorridorInspection.metrics.stairs.value') }}</strong>
                  <span>{{ t('TunnelCorridorInspection.metrics.stairs.title') }}</span>
                </div>
              </article>

              <article class="tunnel-solution__feature-card tunnel-solution__feature-card--metric-wide">
                <div class="tunnel-solution__metric-copy">
                  <strong>{{ t('TunnelCorridorInspection.metrics.range.value') }}</strong>
                  <span>{{ t('TunnelCorridorInspection.metrics.range.title') }}</span>
                </div>
              </article>
            </div>

            <article class="tunnel-solution__feature-card tunnel-solution__feature-card--metric-tall tunnel-solution__feature-card--tether">
              <div class="tunnel-solution__metric-copy">
                <strong>{{ t('TunnelCorridorInspection.metrics.tether.value') }}</strong>
                <span>{{ t('TunnelCorridorInspection.metrics.tether.line1') }}</span>
                <span>{{ t('TunnelCorridorInspection.metrics.tether.line2') }}</span>
              </div>
              <img class="tunnel-solution__feature-icon-image" :src="featureTetherIcon" alt="" aria-hidden="true" />
            </article>
          </div>
        </div>
      </div>
    </section>

    <section class="tunnel-solution__section tunnel-solution__hardware">
      <div class="pixso-container tunnel-solution__section-head tunnel-solution__section-head--hardware">
        <p class="pixso-eyebrow">{{ t('TunnelCorridorInspection.sections.hardware.eyebrow') }}</p>
        <h2 class="tunnel-solution__split-title" data-text-reveal="inline">
          <span class="pixso-heading-xl">{{ t('TunnelCorridorInspection.sections.hardware.title') }}</span>
        </h2>
        <p
          class="tunnel-solution__section-desc pixso-body-lg"
          data-text-reveal="inline"
          v-html="hardwareDescriptionHtml"
        ></p>
        <PixsoButton class="tunnel-solution__hardware-cta" :text="t('TunnelCorridorInspection.sections.hardware.cta')" to="/about/contact" />
      </div>

      <HardwareCalloutScroller
        class="tunnel-solution__hardware-callout"
        :image-src="hardwareImage"
        :image-alt="t('TunnelCorridorInspection.alt.hardwareImage')"
        :aria-label="t('TunnelCorridorInspection.alt.hardwareImage')"
        :items="hardwareItems"
        :image-layout="hardwareImageLayout"
        :image-frames="hardwareAnimationFrames"
        stage-aspect-ratio="16 / 9"
        viewport-fit
        viewport-height="calc(100vh - var(--pixso-nav-height) - 32px)"
        viewport-inset="clamp(72px, 7vw, 160px)"
        :scroll-step="430"
        :frame-scroll-step="1200"
        mobile-image-width="var(--tunnel-hardware-mobile-image-width)"
        :mobile-replay-label="t('TunnelCorridorInspection.sections.hardware.mobileReplayLabel')"
        :scroll-hint-src="scrollHintIcon"
        scroll-hint-placement="viewport"
        scroll-hint-bottom="6vh"
      />
    </section>

    <section class="tunnel-solution__section tunnel-solution__control">
      <div class="pixso-container tunnel-solution__section-head">
        <p class="pixso-eyebrow">{{ t('TunnelCorridorInspection.sections.control.eyebrow') }}</p>
        <h2 class="tunnel-solution__split-title" data-text-reveal="inline">
          <span class="pixso-heading-xl">{{ t('TunnelCorridorInspection.sections.control.title') }}</span>
        </h2>
        <p class="tunnel-solution__section-desc tunnel-solution__section-desc--wide pixso-body-lg">
          {{ t('TunnelCorridorInspection.sections.control.description') }}
        </p>
      </div>

      <div class="pixso-container">
        <TabbedVideoPlayer
          :items="capabilityVideos"
          :poster="isEnglish ? tunnelHowVideoPosterEn : tunnelHowVideoPosterZh"
          :caption="t('TunnelCorridorInspection.videoNote')"
          :tablist-label="t('TunnelCorridorInspection.sections.control.tablistAria')"
        />
      </div>
    </section>

    <section class="tunnel-solution__section tunnel-solution__platform">
      <SectionHeader
        :eyebrow="t('TunnelCorridorInspection.sections.platform.eyebrow')"
        :accent="t('TunnelCorridorInspection.sections.platform.titleAccent')"
        :title="t('TunnelCorridorInspection.sections.platform.title')"
        :description="platformDescription"
      />

      <div class="tunnel-solution__wide tunnel-solution__platform-grid">
        <PlatformGrid
          :items="platformGridItems"
          :aria-label="t('TunnelCorridorInspection.sections.platform.title')"
        />
      </div>
    </section>

    <section class="tunnel-solution__section tunnel-solution__application tunnel-solution__section--last">
      <div class="pixso-container tunnel-solution__section-head">
        <h2 class="tunnel-solution__split-title" data-text-reveal="inline">
          <span class="pixso-heading-xl">{{ t('TunnelCorridorInspection.sections.application.title') }}</span>
        </h2>
      </div>

      <div class="tunnel-solution__wide tunnel-solution__scenario-grid">
        <ScenarioGrid
          :items="scenarioGridItems"
          :aria-label="t('TunnelCorridorInspection.sections.application.title')"
        />
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed, defineComponent, h, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AccordionImageGallery from '@/components/pixso/AccordionImageGallery.vue'
import PixsoButton from '@/components/pixso/PixsoButton.vue'
import HardwareCalloutScroller from '@/components/pixso/HardwareCalloutScroller.vue'
import PlatformGrid from '@/components/pixso/PlatformGrid.vue'
import ScenarioGrid from '@/components/pixso/ScenarioGrid.vue'
import TabbedVideoPlayer from '@/components/pixso/TabbedVideoPlayer.vue'
import { formatSmartTextHtml } from '@/utils/formatSmartTextHtml'
import { bindPinnedVideoFullscreen } from '@/js/usePinnedVideoFullscreen'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

import painRiskImage from '@/assets/images/pixso-tunnel-solution/pain-risk.jpg'
import painBlindImage from '@/assets/images/pixso-tunnel-solution/pain-blind.jpg'
import painLowEfficiencyImage from '@/assets/images/pixso-tunnel-solution/pain-low-efficiency.jpg'
import painEnduranceImage from '@/assets/images/pixso-tunnel-solution/pain-endurance.jpg'
import featureDogDroneImage from '@/assets/images/pixso-tunnel-html/Rectangle_564_303.png'
import featureAiImage from '@/assets/images/pixso-tunnel-feature/ai-recognition.jpg'
import featureSlamImage from '@/assets/images/pixso-tunnel-feature/slam.jpg'
import featureTemperatureImage from '@/assets/images/pixso-tunnel-feature/temperature.jpg'
import featureStairsImage from '@/assets/images/pixso-tunnel-feature/stairs.jpg'
import featureSemanticIcon from '@/assets/images/pixso-tunnel-html/svg10.svg'
import featureSdkIcon from '@/assets/images/pixso-tunnel-html/svg11.svg'
import featureTetherIcon from '@/assets/images/pixso-tunnel-html/svg21.svg'
import chargeIcon from '@/assets/images/pixso-tunnel-html/svg19.svg'
import hardwareImage from '@/assets/images/pixso-tunnel-html/xun52911.png'
import scrollHintIcon from '@/assets/images/pixso-common/xiahua.svg'
import scenarioHighwayTunnelImage from '@/assets/images/pixso-tunnel-scenarios/highway-tunnel.jpg'
import scenarioRailwayTunnelImage from '@/assets/images/pixso-tunnel-scenarios/railway-tunnel.jpg'
import scenarioUrbanRailTunnelImage from '@/assets/images/pixso-tunnel-scenarios/urban-rail-tunnel.jpg'
import scenarioUtilityCorridorImage from '@/assets/images/pixso-tunnel-scenarios/utility-corridor.jpg'
import scenarioWaterConservancyTunnelImage from '@/assets/images/pixso-tunnel-scenarios/water-conservancy-tunnel.jpg'
import scenarioMineTunnelImage from '@/assets/images/pixso-tunnel-scenarios/mine-tunnel.jpg'
import platformTaskImage from '@/assets/images/pixso-industrial-solution/platform-task.jpg'
import platformControlImage from '@/assets/images/pixso-industrial-solution/platform-control.jpg'
import platformAlarmImage from '@/assets/images/pixso-industrial-solution/platform-alarm.jpg'
import platformDataImage from '@/assets/images/pixso-industrial-solution/platform-data.jpg'
import platformAlgorithmImage from '@/assets/images/pixso-industrial-solution/platform-algorithm.jpg'
import platformDeviceImage from '@/assets/images/pixso-industrial-solution/platform-device.jpg'
import tunnelHowVideoPosterZh from '@/assets/images/pixso-tunnel-how/video-poster.jpg'
import tunnelHowVideoOverlayImage from '@/assets/images/pixso-tunnel-how/how-video-overlay.jpg'
import tunnelHowVideoPosterEn from '@/assets/i18n/en/solution/tunnel/tunnel-tether-en-poster.jpg'
import airGroundBannerVideo from '@/assets/videos/pixso-tunnel/air-ground-banner.mp4'
import airGroundCooperationVideoZh from '@/assets/videos/pixso-tunnel/air-ground-cooperation.mp4'
import airGroundCooperationVideoEn from '@/assets/i18n/en/solution/tunnel/air-ground-cooperation.mp4'
import longEnduranceVideoZh from '@/assets/videos/pixso-tunnel/long-endurance.mp4'
import longEnduranceVideoEn from '@/assets/i18n/en/solution/tunnel/long-endurance.mp4'
import autonomousCruiseVideoZh from '@/assets/videos/pixso-tunnel/autonomous-cruise.mp4'
import autonomousCruiseVideoEn from '@/assets/i18n/en/solution/tunnel/autonomous-cruise.mp4'
import threeDimensionalMappingVideoZh from '@/assets/videos/pixso-tunnel/three-dimensional-mapping.mp4'
import threeDimensionalMappingVideoEn from '@/assets/i18n/en/solution/tunnel/three-dimensional-mapping.mp4'
import realtimeVideoZh from '@/assets/videos/pixso-tunnel/realtime-video.mp4'
import realtimeVideoEn from '@/assets/i18n/en/solution/tunnel/realtime-video.mp4'
import intelligentDiagnosisVideoZh from '@/assets/videos/pixso-tunnel/intelligent-diagnosis.mp4'
import intelligentDiagnosisVideoEn from '@/assets/i18n/en/solution/tunnel/intelligent-diagnosis.mp4'
import tunnelTetherVideo from '@/assets/videos/pixso-tunnel/tunnel-tether.mp4'
import tunnelTetherVideoEn from '@/assets/i18n/en/solution/tunnel/tunnel-tether-en.mp4'

const hardwareAnimationFrameContext = require
  .context('@/assets/images/pixso-tunnel-html/animation-frame-2', false, /xun5-2-\d+_updated\.jpg$/)

const hardwareAnimationFrames = hardwareAnimationFrameContext
  .keys()
  .sort((a, b) => Number(a.match(/xun5-2-(\d+)_updated/)?.[1] || 0) - Number(b.match(/xun5-2-(\d+)_updated/)?.[1] || 0))
  .map(hardwareAnimationFrameContext)

const SectionHeader = defineComponent({
  name: 'TunnelSectionHeader',
  props: {
    eyebrow: { type: String, default: '' },
    accent: { type: String, default: '' },
    title: { type: String, required: true },
    description: { type: String, default: '' }
  },
  setup(props) {
    return () => h('div', { class: 'pixso-container tunnel-solution__section-head' }, [
      props.eyebrow ? h('p', { class: 'pixso-eyebrow' }, props.eyebrow) : null,
      h('h2', { class: 'tunnel-solution__split-title', 'data-text-reveal': 'inline' }, [
        props.accent ? h('span', { class: 'tunnel-solution__title-accent pixso-heading-xl' }, props.accent) : null,
        h('span', { class: 'pixso-heading-xl' }, props.title)
      ]),
      props.description ? h('p', { class: 'tunnel-solution__section-desc tunnel-solution__section-desc--wide pixso-body-lg' }, props.description) : null
    ])
  }
})

const { t, locale } = useI18n()
const isEnglish = computed(() => locale.value === 'en')
const howSectionRef = ref(null)
const howVideoCardRef = ref(null)
const howVideoRef = ref(null)
const isHowVideoInteractive = ref(false)
const showHowVideoOverlay = ref(true)

const HOW_VIDEO_HIDDEN_DURATION = 0.04
const HOW_VIDEO_REVEAL_DURATION = 0.14
const HOW_VIDEO_HOLD_DURATION = 0.12
const HOW_VIDEO_TIMELINE_DURATION = HOW_VIDEO_HIDDEN_DURATION + HOW_VIDEO_REVEAL_DURATION + HOW_VIDEO_HOLD_DURATION
const HOW_VIDEO_INTERACTIVE_PROGRESS = (
  HOW_VIDEO_HIDDEN_DURATION + HOW_VIDEO_REVEAL_DURATION
) / HOW_VIDEO_TIMELINE_DURATION
const HOW_VIDEO_PIN_END = '+=60%'

const howVideoOverlayStyle = computed(() => ({
  backgroundImage: `url(${tunnelHowVideoOverlayImage})`
}))

let howRevealTimeline = null
let howVideoFullscreenCleanup = null

const playHowVideo = async () => {
  const video = howVideoRef.value

  if (!video) {
    return
  }

  showHowVideoOverlay.value = false

  try {
    await video.play()
  } catch {
    showHowVideoOverlay.value = true
  }
}

const bindHowVideoFullscreenSupport = (video) => {
  howVideoFullscreenCleanup?.()
  howVideoFullscreenCleanup = bindPinnedVideoFullscreen(video, {
    getScrollTrigger: () => howRevealTimeline?.scrollTrigger,
    getSettleTargets: () => [howSectionRef.value, howVideoCardRef.value]
  })
}

onMounted(() => {
  const section = howSectionRef.value
  const videoCard = howVideoCardRef.value
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  if (!section || !videoCard) {
    bindHowVideoFullscreenSupport(howVideoRef.value)
    return
  }

  if (reducedMotion) {
    gsap.set(videoCard, { autoAlpha: 1, y: 0, scale: 1, pointerEvents: 'auto' })
    isHowVideoInteractive.value = true
    bindHowVideoFullscreenSupport(howVideoRef.value)
    return
  }

  gsap.set(videoCard, {
    autoAlpha: 0,
    y: 96,
    scale: 0.94,
    pointerEvents: 'none'
  })

  howRevealTimeline = gsap.timeline({
    scrollTrigger: {
      trigger: section,
      start: 'top top',
      end: HOW_VIDEO_PIN_END,
      scrub: true,
      pin: true,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        isHowVideoInteractive.value = self.progress >= HOW_VIDEO_INTERACTIVE_PROGRESS
      }
    }
  })

  howRevealTimeline
    .to(videoCard, { autoAlpha: 0, y: 96, scale: 0.94, duration: HOW_VIDEO_HIDDEN_DURATION, ease: 'none' })
    .to(videoCard, { autoAlpha: 1, y: 0, scale: 1, pointerEvents: 'auto', duration: HOW_VIDEO_REVEAL_DURATION, ease: 'power2.out' })
    .to(videoCard, { autoAlpha: 1, y: 0, scale: 1, duration: HOW_VIDEO_HOLD_DURATION, ease: 'none' })

  bindHowVideoFullscreenSupport(howVideoRef.value)
})

watch(isEnglish, async () => {
  await nextTick()
  bindHowVideoFullscreenSupport(howVideoRef.value)
})

onBeforeUnmount(() => {
  howVideoFullscreenCleanup?.()
  howVideoFullscreenCleanup = null
  howRevealTimeline?.scrollTrigger?.kill()
  howRevealTimeline?.kill()
})

const featureTitle = computed(() => (
  `${t('TunnelCorridorInspection.sections.features.titlePrefix')}${t('TunnelCorridorInspection.sections.features.titleNumber')}${t('TunnelCorridorInspection.sections.features.title')}`
))

const platformDescription = computed(() => (
  `${t('TunnelCorridorInspection.sections.platform.descriptionPrefix')}${t('TunnelCorridorInspection.sections.platform.descriptionHighlight')}${t('TunnelCorridorInspection.sections.platform.descriptionSuffix')}`
))

const hardwareDescriptionHtml = computed(() => (
  formatSmartTextHtml(
    t('TunnelCorridorInspection.sections.hardware.description'),
    [],
    'tunnel-solution__nowrap'
  )
))

const painGalleryItems = computed(() => [
  {
    key: 'risk',
    initial: t('TunnelCorridorInspection.painItems.risk.initial'),
    title: t('TunnelCorridorInspection.painItems.risk.title'),
    detail: t('TunnelCorridorInspection.painItems.risk.description'),
    image: painRiskImage,
    imageAlt: t('TunnelCorridorInspection.alt.painImage')
  },
  {
    key: 'blind',
    initial: t('TunnelCorridorInspection.painItems.blind.initial'),
    title: t('TunnelCorridorInspection.painItems.blind.title'),
    detail: t('TunnelCorridorInspection.painItems.blind.description'),
    image: painBlindImage,
    imageAlt: t('TunnelCorridorInspection.alt.featureSceneImage')
  },
  {
    key: 'low',
    initial: t('TunnelCorridorInspection.painItems.low.initial'),
    title: t('TunnelCorridorInspection.painItems.low.title'),
    detail: t('TunnelCorridorInspection.painItems.low.description'),
    image: painLowEfficiencyImage,
    imageAlt: t('TunnelCorridorInspection.alt.featureMainImage')
  },
  {
    key: 'hard',
    initial: t('TunnelCorridorInspection.painItems.hard.initial'),
    title: t('TunnelCorridorInspection.painItems.hard.title'),
    detail: t('TunnelCorridorInspection.painItems.hard.description'),
    detailHighlights: [t('TunnelCorridorInspection.painItems.hard.highlight')],
    image: painEnduranceImage,
    imageAlt: t('TunnelCorridorInspection.alt.featureModuleImage')
  }
])

const hardwareImageLayout = {
  left: 0,
  top: 0,
  width: 100,
  height: 100,
  objectFit: 'cover'
}

const hardwareItems = computed(() => [
  {
    key: 'dual',
    title: t('TunnelCorridorInspection.hardware.dual.title'),
    note: { x: 'var(--pixso-page-padding)', y: 13, width: 28 },
    dot: { x: 40.2, y: 47 },
    line: {
      from: { anchor: 'title-end', gap: 14 },
      to: { x: 40.2, y: 47 }
    },
    lines: [
      [{ text: t('TunnelCorridorInspection.hardware.dual.line1') }],
      [{ text: t('TunnelCorridorInspection.hardware.dual.line2') }],
      [{ text: t('TunnelCorridorInspection.hardware.dual.line3') }]
    ]
  },
  {
    key: 'drone',
    title: t('TunnelCorridorInspection.hardware.drone.title'),
    side: 'right',
    note: { right: 'var(--pixso-page-padding)', y: 4, width: 28 },
    dot: { x: 53.9, y: 14.4 },
    line: {
      from: { anchor: 'title-start', gap: 14 },
      to: { x: 53.9, y: 14.4 }
    },
    lines: [
      [{ text: t('TunnelCorridorInspection.hardware.drone.line1') }],
      [{ text: t('TunnelCorridorInspection.hardware.drone.line2') }]
    ]
  },
  {
    key: 'dock',
    title: t('TunnelCorridorInspection.hardware.dock.title'),
    side: 'right',
    note: { right: 'var(--pixso-page-padding)', y: 42, width: 28 },
    dot: { x: 56.9, y: 45.9 },
    line: {
      from: { anchor: 'title-start', gap: 14 },
      to: { x: 56.9, y: 45.9 }
    },
    lines: [
      [{ text: t('TunnelCorridorInspection.hardware.dock.line1') }],
      [{ text: t('TunnelCorridorInspection.hardware.dock.line2') }],
      [{ text: t('TunnelCorridorInspection.hardware.dock.line3') }]
    ]
  },
  {
    key: 'lidar',
    title: t('TunnelCorridorInspection.hardware.lidar.title'),
    note: { x: 'var(--pixso-page-padding)', y: 64, width: 28 },
    dot: { x: 42.2, y: 64.8 },
    line: {
      from: { anchor: 'title-end', gap: 14 },
      to: { x: 42.2, y: 64.8 }
    },
    lines: [
      [{ text: t('TunnelCorridorInspection.hardware.lidar.line1') }],
      [{ text: t('TunnelCorridorInspection.hardware.lidar.line2') }],
      [{ text: t('TunnelCorridorInspection.hardware.lidar.line3') }]
    ]
  },
  {
    key: 'edge',
    title: t('TunnelCorridorInspection.hardware.edge.title'),
    side: 'right',
    note: { right: 'var(--pixso-page-padding)', y: 70, width: 28 },
    dot: { x: 55.1, y: 51.8 },
    line: {
      from: { anchor: 'title-start', gap: 14 },
      to: { x: 55.1, y: 51.8 }
    },
    lines: [
      [{ text: t('TunnelCorridorInspection.hardware.edge.line1') }],
      [
        { text: t('TunnelCorridorInspection.hardware.edge.line2Prefix') },
        { text: t('TunnelCorridorInspection.hardware.edge.line2Highlight'), highlight: true },
        { text: t('TunnelCorridorInspection.hardware.edge.line2Suffix') }
      ],
      [
        { text: t('TunnelCorridorInspection.hardware.edge.line3Prefix') },
        { text: t('TunnelCorridorInspection.hardware.edge.line3Highlight'), highlight: true },
        { text: t('TunnelCorridorInspection.hardware.edge.line3Suffix') }
      ]
    ]
  }
])

const capabilityVideos = computed(() => [
  {
    key: 'cooperation',
    buttonLabel: t('TunnelCorridorInspection.capabilityTabs.cooperation'),
    ariaLabel: t('TunnelCorridorInspection.alt.controlImage'),
    src: isEnglish.value ? airGroundCooperationVideoEn : airGroundCooperationVideoZh
  },
  {
    key: 'endurance',
    buttonLabel: t('TunnelCorridorInspection.capabilityTabs.endurance'),
    ariaLabel: t('TunnelCorridorInspection.alt.controlImage'),
    src: isEnglish.value ? longEnduranceVideoEn : longEnduranceVideoZh
  },
  {
    key: 'patrol',
    buttonLabel: t('TunnelCorridorInspection.capabilityTabs.patrol'),
    ariaLabel: t('TunnelCorridorInspection.alt.controlImage'),
    src: isEnglish.value ? autonomousCruiseVideoEn : autonomousCruiseVideoZh
  },
  {
    key: 'mapping',
    buttonLabel: t('TunnelCorridorInspection.capabilityTabs.mapping'),
    ariaLabel: t('TunnelCorridorInspection.alt.controlImage'),
    src: isEnglish.value ? threeDimensionalMappingVideoEn : threeDimensionalMappingVideoZh
  },
  {
    key: 'video',
    buttonLabel: t('TunnelCorridorInspection.capabilityTabs.video'),
    ariaLabel: t('TunnelCorridorInspection.alt.controlImage'),
    src: isEnglish.value ? realtimeVideoEn : realtimeVideoZh
  },
  {
    key: 'diagnosis',
    buttonLabel: t('TunnelCorridorInspection.capabilityTabs.diagnosis'),
    ariaLabel: t('TunnelCorridorInspection.alt.controlImage'),
    src: isEnglish.value ? intelligentDiagnosisVideoEn : intelligentDiagnosisVideoZh
  }
])

const platformCards = [
  {
    key: 'task',
    image: platformTaskImage,
    titleKey: 'IndustrialInspection.platformCards.task',
    altKey: 'IndustrialInspection.platformCards.task'
  },
  {
    key: 'control',
    image: platformControlImage,
    titleKey: 'IndustrialInspection.platformCards.control',
    altKey: 'IndustrialInspection.platformCards.control'
  },
  {
    key: 'alarm',
    image: platformAlarmImage,
    titleKey: 'IndustrialInspection.platformCards.alarm',
    altKey: 'IndustrialInspection.platformCards.alarm'
  },
  {
    key: 'data',
    image: platformDataImage,
    titleKey: 'IndustrialInspection.platformCards.data',
    altKey: 'IndustrialInspection.platformCards.data'
  },
  {
    key: 'algorithm',
    image: platformAlgorithmImage,
    titleKey: 'IndustrialInspection.platformCards.algorithm',
    altKey: 'IndustrialInspection.platformCards.algorithm'
  },
  {
    key: 'device',
    image: platformDeviceImage,
    titleKey: 'IndustrialInspection.platformCards.device',
    altKey: 'IndustrialInspection.platformCards.device'
  }
]

const platformGridItems = computed(() => (
  platformCards.map((item) => ({
    key: item.key,
    title: t(item.titleKey),
    image: item.image,
    alt: t(item.altKey)
  }))
))

const scenarioCards = [
  {
    key: 'highwayTunnel',
    image: scenarioHighwayTunnelImage,
    titleKey: 'TunnelCorridorInspection.scenarioCards.highwayTunnel.title',
    altKey: 'TunnelCorridorInspection.scenarioCards.highwayTunnel.alt'
  },
  {
    key: 'railwayTunnel',
    image: scenarioRailwayTunnelImage,
    titleKey: 'TunnelCorridorInspection.scenarioCards.railwayTunnel.title',
    altKey: 'TunnelCorridorInspection.scenarioCards.railwayTunnel.alt'
  },
  {
    key: 'urbanRailTunnel',
    image: scenarioUrbanRailTunnelImage,
    titleKey: 'TunnelCorridorInspection.scenarioCards.urbanRailTunnel.title',
    altKey: 'TunnelCorridorInspection.scenarioCards.urbanRailTunnel.alt'
  },
  {
    key: 'utilityCorridor',
    image: scenarioUtilityCorridorImage,
    titleKey: 'TunnelCorridorInspection.scenarioCards.utilityCorridor.title',
    altKey: 'TunnelCorridorInspection.scenarioCards.utilityCorridor.alt'
  },
  {
    key: 'waterConservancyTunnel',
    image: scenarioWaterConservancyTunnelImage,
    titleKey: 'TunnelCorridorInspection.scenarioCards.waterConservancyTunnel.title',
    altKey: 'TunnelCorridorInspection.scenarioCards.waterConservancyTunnel.alt'
  },
  {
    key: 'mineTunnel',
    image: scenarioMineTunnelImage,
    titleKey: 'TunnelCorridorInspection.scenarioCards.mineTunnel.title',
    altKey: 'TunnelCorridorInspection.scenarioCards.mineTunnel.alt'
  }
]

const scenarioGridItems = computed(() => (
  scenarioCards.map((item) => ({
    key: item.key,
    title: t(item.titleKey),
    image: item.image,
    alt: t(item.altKey)
  }))
))
</script>

<style scoped>
.tunnel-solution {
  display: block;
  padding-bottom: var(--tunnel-page-bottom-space);
  --tunnel-card-radius: var(--pixso-radius-lg);
  --tunnel-card-radius-soft: calc(var(--pixso-radius-lg) - var(--pixso-gap-xs));
  --tunnel-hardware-mobile-image-width: min(100%, clamp(320px, 78vw, 520px));
  --tunnel-section-space: var(--pixso-section-gap-xl);
  --tunnel-section-head-max: 1200px;
  --tunnel-page-bottom-space: var(--pixso-section-gap-xl);
}

.tunnel-solution__hero {
  position: relative;
  width: 100vw;
  height: var(--pixso-hero-height);
  min-height: var(--pixso-hero-min);
  margin-left: calc(50% - 50vw);
  overflow: hidden;
  background: var(--pixso-color-bg);
}

.tunnel-solution__hero::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.24) 0%, rgba(0, 0, 0, 0.08) 52%, rgba(0, 0, 0, 0.48) 100%);
  pointer-events: none;
}

.tunnel-solution__hero-video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.tunnel-solution__hero-copy {
  position: absolute;
  z-index: 2;
  top: clamp(96px, 12vw, 172px);
  left: 50%;
  transform: translateX(-50%);
  width: min(calc(100% - var(--pixso-page-padding)), 1920px);
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--pixso-color-text);
  text-align: center;
  pointer-events: none;
}

.tunnel-solution__hero-kicker {
  margin: 8px 0 0;
  font-size: var(--pixso-fs-64);
  line-height: var(--pixso-lh-64);
  font-weight: 700;
  text-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
}

.tunnel-solution__hero-title {
  margin: 0;
  font-size: var(--pixso-fs-96);
  font-weight: 700;
  line-height: var(--pixso-lh-96);
  text-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
}

.tunnel-solution__section {
  padding-top: var(--tunnel-section-space);
}

.tunnel-solution__section--last {
  padding-bottom: 0;
}

.tunnel-solution__section-head {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--tunnel-section-head-max));
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--pixso-gap-sm);
  text-align: center;
}

.tunnel-solution__section-head.tunnel-solution__section-head--hardware {
  gap: 0;
}

.tunnel-solution__section-head--hardware .tunnel-solution__split-title,
.tunnel-solution__section-head--hardware .tunnel-solution__section-desc {
  margin-top: var(--pixso-gap-sm);
}

.tunnel-solution__hardware-cta {
  margin-top: var(--pixso-gap-lg);
}

.tunnel-solution__split-title {
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
}

.tunnel-solution :deep(.tunnel-solution__split-title) {
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
}

.tunnel-solution__split-title > span,
.tunnel-solution :deep(.tunnel-solution__split-title > span) {
  font-family: var(--pixso-font-cn);
  font-weight: 700;
  font-size: clamp(var(--pixso-fs-48), 5vw, var(--pixso-fs-96));
  line-height: clamp(var(--pixso-lh-48), 6vw, var(--pixso-lh-96));
}

.tunnel-solution__title-accent {
  color: var(--pixso-color-primary);
}

.tunnel-solution :deep(.tunnel-solution__title-accent) {
  color: var(--pixso-color-primary);
}

.tunnel-solution__section-desc {
  max-width: 960px;
  margin: var(--pixso-gap-xs) 0 0;
  color: var(--pixso-color-muted);
  font-size: clamp(var(--pixso-fs-16), 1.25vw, var(--pixso-fs-20));
  line-height: clamp(var(--pixso-lh-16), 1.875vw, var(--pixso-lh-20));
}

.tunnel-solution__section-desc--wide {
  max-width: 1120px;
}

.tunnel-solution__section-desc :deep(.tunnel-solution__nowrap) {
  display: inline-block;
  white-space: nowrap;
}

.tunnel-solution__wide {
  width: min(calc(100% - var(--pixso-page-padding) * 2), 1792px);
  margin-inline: auto;
}

.tunnel-solution__pain-shell {
  margin-top: clamp(48px, 4vw, 64px);
}

.tunnel-solution__how {
  /* Lock how section to one viewport so the pinned reveal never crops the video. */
  position: relative;
  height: 100vh;
  height: 100svh;
  min-height: 100vh;
  min-height: 100svh;
  max-height: 100vh;
  max-height: 100svh;
  margin-top: var(--tunnel-section-space);
  padding-top: 0;
  box-sizing: border-box;
  overflow: hidden;
  background:
    linear-gradient(180deg, #000 0%, rgba(0, 0, 0, 0.72) 18%, rgba(0, 0, 0, 0.16) 48%, rgba(0, 0, 0, 0.78) 100%),
    url("@/assets/images/pixso-tunnel-how/how-background.jpg") center/cover no-repeat;
}

.tunnel-solution__how-stage {
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(16px, 3vh, 48px);
  padding-block: clamp(64px, 8vh, 112px) clamp(12px, 2.5vh, 40px);
}

/*
 * Video shell sizes itself from both width and remaining viewport height.
 * width:auto + max-width + max-height + aspect-ratio lets the browser pick a
 * 16:9 box that always fits under the title within one screen.
 */
.tunnel-solution__how-card-shell {
  --tunnel-how-video-max-height: min(48vh, 540px, calc(100svh - 26rem));
  flex: 0 1 auto;
  min-width: 0;
  min-height: 0;
  width: auto;
  max-width: min(
    calc(100% - var(--pixso-gap-lg)),
    clamp(480px, 48vw, 860px),
    calc(var(--tunnel-how-video-max-height) * 16 / 9)
  );
  height: auto;
  max-height: var(--tunnel-how-video-max-height);
  aspect-ratio: 16 / 9;
}

.tunnel-solution__how-video-card {
  position: relative;
  width: 100%;
  height: 100%;
  margin: 0;
  overflow: hidden;
  border-radius: var(--tunnel-card-radius-soft);
  background: var(--pixso-color-surface);
  box-shadow: 0 24px 90px rgba(0, 0, 0, 0.45);
  opacity: 0;
  transform: translate3d(0, 96px, 0) scale(0.94);
  transform-origin: center bottom;
  pointer-events: none;
  will-change: opacity, transform;
}

.tunnel-solution__how-video-card[aria-hidden="true"] {
  pointer-events: none;
}

.tunnel-solution__how-video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.tunnel-solution__how-video-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  border-radius: inherit;
  overflow: hidden;
  background-color: #000;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  cursor: pointer;
}

.tunnel-solution__how-video-overlay::before {
  content: '';
  width: clamp(72px, 8vw, 108px);
  aspect-ratio: 1;
  border: 8px solid rgba(255, 255, 255, 0.92);
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.16);
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.32);
}

.tunnel-solution__how-video-overlay::after {
  content: '';
  position: absolute;
  left: calc(50% + clamp(4px, 0.45vw, 7px));
  top: 50%;
  width: 0;
  height: 0;
  border-top: clamp(18px, 2vw, 28px) solid transparent;
  border-bottom: clamp(18px, 2vw, 28px) solid transparent;
  border-left: clamp(28px, 3vw, 42px) solid rgba(255, 255, 255, 0.96);
  transform: translate(-50%, -50%);
}

.tunnel-solution__how-video-overlay:focus-visible {
  outline: 2px solid var(--pixso-color-primary);
  outline-offset: -6px;
}

.tunnel-solution__feature-grid {
  margin-top: clamp(48px, 4vw, 64px);
  display: flex;
  flex-direction: column;
  gap: var(--pixso-gap-md);
}

.tunnel-solution__feature-top-row,
.tunnel-solution__feature-bottom-row,
.tunnel-solution__feature-mini-row {
  display: grid;
  gap: var(--pixso-gap-md);
  align-items: stretch;
}

.tunnel-solution__feature-top-row {
  min-height: 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.tunnel-solution__feature-bottom-row {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.tunnel-solution__feature-right-stack,
.tunnel-solution__feature-column,
.tunnel-solution__feature-bottom-pair {
  min-width: 0;
  display: grid;
  gap: var(--pixso-gap-md);
}

.tunnel-solution__feature-bottom-pair--left {
  grid-template-columns: 320fr 540fr;
}

.tunnel-solution__feature-bottom-pair--right {
  grid-template-columns: 540fr 320fr;
}

.tunnel-solution__feature-right-stack {
  min-height: 0;
  grid-template-rows: 323fr 283fr;
}

.tunnel-solution__feature-mini-row {
  min-height: clamp(190px, 14.7vw, 272px);
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.tunnel-solution__feature-column {
  grid-template-rows: repeat(2, minmax(0, 1fr));
}

.tunnel-solution__feature-card {
  position: relative;
  min-width: 0;
  overflow: hidden;
  border-radius: var(--tunnel-card-radius);
  background: linear-gradient(180deg, var(--pixso-color-surface) 0%, #282938 100%);
  color: var(--pixso-color-text);
  /* Enable cqw so icon clamp matches FeatureMosaicCard (巡1) scaling. */
  container-type: inline-size;
}

.tunnel-solution__feature-card > img:not(.tunnel-solution__feature-icon-image) {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.tunnel-solution__feature-card--stairs > img:not(.tunnel-solution__feature-icon-image) {
  width: 112%;
  max-width: none;
  object-position: center bottom;
}

.tunnel-solution__feature-card--image-wide::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 1;
  background: rgba(0, 0, 0, 0.22);
  pointer-events: none;
}

.tunnel-solution__feature-card--hero {
  width: 100%;
  align-self: flex-start;
  aspect-ratio: 884 / 630;
}

.tunnel-solution__feature-card--ai {
  aspect-ratio: 884 / 323;
}

.tunnel-solution__feature-card--slam,
.tunnel-solution__feature-card--copy {
  aspect-ratio: 278.666 / 283;
}

.tunnel-solution__feature-card--hero::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 1;
  background:
    linear-gradient(180deg, rgba(0, 0, 0, 0.18) 0%, rgba(0, 0, 0, 0.16) 46%, rgba(0, 0, 0, 0.38) 100%),
    linear-gradient(90deg, rgba(0, 0, 0, 0.14) 0%, rgba(0, 0, 0, 0) 40%, rgba(0, 0, 0, 0.14) 100%);
  pointer-events: none;
}

.tunnel-solution__feature-card--ai::after,
.tunnel-solution__feature-card--slam::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
}

.tunnel-solution__feature-card--ai::after {
  background:
    linear-gradient(180deg, rgba(0, 0, 0, 0.34) 0%, rgba(0, 0, 0, 0.48) 48%, rgba(0, 0, 0, 0.66) 100%),
    radial-gradient(circle at 50% 50%, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.42) 100%);
}

.tunnel-solution__feature-card--slam::after {
  background:
    linear-gradient(180deg, rgba(0, 0, 0, 0.56) 0%, rgba(0, 0, 0, 0.22) 48%, rgba(0, 0, 0, 0.68) 100%),
    linear-gradient(90deg, rgba(0, 0, 0, 0.46) 0%, rgba(0, 0, 0, 0.12) 62%, rgba(0, 0, 0, 0.32) 100%);
}

.tunnel-solution__feature-card--ai,
.tunnel-solution__feature-card--slam,
.tunnel-solution__feature-card--copy,
.tunnel-solution__feature-card--metric-wide {
  min-height: 0;
}

.tunnel-solution__feature-card--copy {
  padding: var(--pixso-gap-md);
  display: block;
  height: auto;
  min-height: 0;
  aspect-ratio: auto;
  background:
    linear-gradient(180deg, rgba(22, 22, 23, 1) 0%, rgba(25, 25, 31, 1) 50%, rgba(27, 28, 38, 1) 100%);
}

.tunnel-solution__feature-card--copy > div {
  /* Reserve room for 48px icon (FeatureMosaicCard max). */
  padding-bottom: calc(var(--pixso-gap-xs) + 48px);
}

/* The text cards may need to grow when their columns become narrow. Let the
 * row absorb that extra height and keep the image card inside its own grid
 * column instead of allowing its fixed ratio to make it overflow sideways. */
.tunnel-solution__feature-card--slam {
  aspect-ratio: auto;
}

.tunnel-solution__feature-card--metric-tall {
  aspect-ratio: 320 / 514;
  background:
    linear-gradient(180deg, rgba(22, 22, 23, 1) 0%, rgba(40, 41, 56, 1) 100%);
}

.tunnel-solution__feature-card--metric-wide {
  aspect-ratio: 540 / 245;
  background:
    linear-gradient(180deg, rgba(22, 22, 23, 1) 0%, rgba(40, 41, 56, 1) 100%);
}

.tunnel-solution__feature-hero-copy,
.tunnel-solution__feature-size,
.tunnel-solution__feature-overlay,
.tunnel-solution__metric-copy {
  position: absolute;
  z-index: 2;
}

.tunnel-solution__feature-hero-copy {
  left: clamp(24px, 3.35vw, 60px);
  right: clamp(24px, 3.35vw, 60px);
  top: var(--pixso-gap-md);
}

.tunnel-solution__feature-hero-copy h3,
.tunnel-solution__feature-overlay h3 {
  margin: 0;
  font-size: clamp(var(--pixso-fs-20), 2.5vw, var(--pixso-fs-48));
  line-height: clamp(var(--pixso-lh-20), 3vw, var(--pixso-lh-48));
  font-weight: 700;
}

.tunnel-solution__feature-hero-copy p,
.tunnel-solution__feature-overlay p,
.tunnel-solution__feature-card--copy p {
  margin: 4px 0 0;
  color: rgba(255, 255, 255, 0.92);
  font-size: clamp(var(--pixso-fs-14), 1.05vw, var(--pixso-fs-20));
  line-height: clamp(var(--pixso-lh-14), 1.55vw, var(--pixso-lh-20));
  font-weight: 500;
}

.tunnel-solution__feature-size {
  left: 50%;
  bottom: var(--pixso-gap-md);
  width: max-content;
  transform: translateX(-50%);
  text-align: center;
}

.tunnel-solution__feature-size strong,
.tunnel-solution__metric-copy strong {
  display: block;
  font-family: var(--pixso-font-en);
  font-size: clamp(var(--pixso-fs-48), 3.75vw, var(--pixso-fs-72));
  line-height: clamp(var(--pixso-lh-48), 4.48vw, var(--pixso-lh-72));
  font-weight: 500;
}

.tunnel-solution__feature-size strong {
  font-size: clamp(var(--pixso-fs-18), 1.25vw, var(--pixso-fs-24));
  line-height: clamp(var(--pixso-lh-18), 1.77vw, var(--pixso-lh-24));
}

.tunnel-solution__feature-size span,
.tunnel-solution__metric-copy span {
  display: block;
  color: var(--pixso-color-text);
  font-size: clamp(var(--pixso-fs-14), 1.05vw, var(--pixso-fs-20));
  line-height: clamp(var(--pixso-lh-14), 1.55vw, var(--pixso-lh-20));
  font-weight: 500;
}

.tunnel-solution__feature-overlay {
  left: var(--pixso-gap-md);
  right: var(--pixso-gap-md);
  bottom: var(--pixso-gap-md);
}

.tunnel-solution__feature-overlay--center {
  inset: 50% var(--pixso-gap-md) auto;
  transform: translateY(-38%);
  text-align: center;
}

.tunnel-solution__feature-overlay--center h3 {
  font-size: var(--pixso-fs-72);
  line-height: var(--pixso-lh-72);
}

.tunnel-solution__feature-card--ai .tunnel-solution__feature-overlay--center h3 {
  font-size: var(--pixso-fs-64);
  line-height: var(--pixso-lh-64);
}

.tunnel-solution__feature-card--ai .tunnel-solution__feature-overlay--center p {
  margin-top: calc(var(--pixso-gap-xs) * 0.5);
  font-size: var(--pixso-fs-20);
  line-height: var(--pixso-lh-20);
}

.tunnel-solution__feature-card--slam .tunnel-solution__feature-overlay {
  left: var(--pixso-gap-sm);
  right: var(--pixso-gap-sm);
  top: var(--pixso-gap-sm);
  bottom: auto;
}

.tunnel-solution__feature-card--slam .tunnel-solution__feature-overlay h3 {
  font-size: var(--pixso-fs-24);
  line-height: var(--pixso-lh-24);
}

.tunnel-solution__feature-card--copy h3 {
  margin: 0;
  font-size: var(--pixso-fs-24);
  line-height: var(--pixso-lh-24);
  font-weight: 700;
}

.tunnel-solution__feature-card--slam .tunnel-solution__feature-overlay p {
  font-size: var(--pixso-fs-16);
  line-height: var(--pixso-lh-16);
}

.tunnel-solution__feature-icon-image,
.tunnel-solution__battery-icon {
  position: absolute;
  z-index: 2;
  right: var(--pixso-gap-md);
  bottom: var(--pixso-gap-md);
  /* Match FeatureMosaicCard icon size: 48px desktop max. */
  width: 48px;
  height: 48px;
}

.tunnel-solution__feature-icon-image {
  display: block;
  object-fit: contain;
}

.tunnel-solution__feature-card--copy .tunnel-solution__feature-icon-image {
  position: absolute;
}

.tunnel-solution__battery-icon {
  display: block;
  /* Same 48px as action icons / battery & stats icons. */
  width: 48px;
  height: 48px;
}

.tunnel-solution__battery-icon-image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
}

.tunnel-solution__metric-copy {
  left: clamp(24px, 3.35vw, 60px);
  right: var(--pixso-gap-md);
  top: clamp(48px, 3.75vw, 66px);
}

.tunnel-solution__feature-card--metric-wide .tunnel-solution__metric-copy {
  top: 50%;
  transform: translateY(-50%);
}

.tunnel-solution__feature-card--image-wide .tunnel-solution__metric-copy {
  top: 50%;
  left: 50%;
  right: auto;
  width: max-content;
  max-width: 82%;
  transform: translate(-50%, -50%);
  text-align: left;
}

.tunnel-solution__feature-card--image-wide .tunnel-solution__metric-copy strong {
  font-size: var(--pixso-fs-64);
  line-height: var(--pixso-lh-64);
  white-space: nowrap;
}

.tunnel-solution__feature-card--temperature .tunnel-solution__metric-copy strong {
  font-size: var(--pixso-fs-48);
  line-height: var(--pixso-lh-48);
}

.tunnel-solution__feature-card--image-wide .tunnel-solution__metric-copy span {
  margin-top: calc(var(--pixso-gap-xs) * 0.5);
  text-align: left;
}

.tunnel-solution__feature-card--stairs .tunnel-solution__metric-copy {
  left: clamp(32px, 4.45vw, 78px);
  right: auto;
  width: auto;
  max-width: 48%;
  transform: translateY(-50%);
  text-align: left;
}

.tunnel-solution__feature-card--duration .tunnel-solution__metric-copy,
.tunnel-solution__feature-card--tether .tunnel-solution__metric-copy {
  top: clamp(48px, 3.75vw, 66px);
}

.tunnel-solution__feature-card--tether .tunnel-solution__metric-copy {
  left: var(--pixso-gap-md);
}

.tunnel-solution__hardware-callout {
  width: 100vw;
  max-width: none;
  box-sizing: border-box;
  margin: clamp(56px, 6vh, 96px) 0 0 calc(50% - 50vw);
}

.tunnel-solution__hardware-callout.hardware-callout-scroller--viewport-fit {
  width: 100vw;
  max-width: none;
}

.tunnel-solution--en :deep(.hardware-callout-scroller__note h3) {
  font-size: var(--pixso-fs-32);
  line-height: var(--pixso-lh-32);
  font-weight: 800;
}

.tunnel-solution--en :deep(.hardware-callout-scroller__note p) {
  font-size: var(--pixso-fs-14);
  line-height: var(--pixso-lh-14);
}

.tunnel-solution--en .tunnel-solution__feature-card--copy h3 {
  font-size: var(--pixso-fs-16);
  line-height: var(--pixso-lh-16);
}

.tunnel-solution--en .tunnel-solution__feature-card--copy p {
  font-size: var(--pixso-fs-12);
  line-height: var(--pixso-lh-12);
}

.tunnel-solution--en .tunnel-solution__feature-card--ai .tunnel-solution__feature-overlay--center h3 {
  font-size: var(--pixso-fs-56);
  line-height: var(--pixso-lh-56);
}

.tunnel-solution--en .tunnel-solution__feature-card--ai .tunnel-solution__feature-overlay--center p {
  font-size: var(--pixso-fs-18);
  line-height: var(--pixso-lh-18);
}

.tunnel-solution__platform-grid,
.tunnel-solution__scenario-grid {
  margin-top: clamp(48px, 4vw, 64px);
}

.tunnel-solution :deep(.pixso-tabbed-video__surface) {
  aspect-ratio: 1128 / 635;
}

.tunnel-solution :deep(.accordion-image-gallery) {
  width: min(100%, 1200px);
}

@media (min-width: 1201px) {
  .tunnel-solution__hero-copy {
    top: 120px;
  }

  .tunnel-solution__hero-kicker {
    margin-top: 0;
  }

  /* If a narrow desktop column makes the copy row taller, keep the hero in
   * the same top-row track so the empty area cannot appear beside it. */
  .tunnel-solution__feature-card--hero {
    align-self: stretch;
    aspect-ratio: auto;
  }
}

@media (max-width: 1200px) {
  .tunnel-solution__hero-copy {
    top: 84px;
    width: min(calc(100% - 32px), 760px);
  }

  .tunnel-solution__hero {
    min-height: var(--pixso-hero-min);
  }

  .tunnel-solution__hero-video {
    min-height: var(--pixso-hero-min);
    object-fit: cover;
    object-position: center top;
  }

  /* Compact stage (<1201): full-bleed hardware media; cards keep page padding. */
  .tunnel-solution__hardware-callout,
  .tunnel-solution__hardware-callout.hardware-callout-scroller--viewport-fit,
  .tunnel-solution__hardware-callout.hardware-callout-scroller--compact,
  .tunnel-solution__hardware-callout.hardware-callout-scroller--compact.hardware-callout-scroller--viewport-fit {
    --tunnel-hardware-mobile-image-width: 100%;
    width: 100vw;
    max-width: none;
    margin-top: clamp(56px, 5vw, 80px);
    margin-left: calc(50% - 50vw);
    margin-right: calc(50% - 50vw);
  }

  .tunnel-solution__hardware-callout :deep(.hardware-callout-scroller__mobile-list) {
    margin-inline: var(--pixso-page-padding);
  }

  .tunnel-solution__hardware-callout :deep(.hardware-callout-scroller__image) {
    margin-inline: 0;
  }

  /* Match the industrial inspection mosaic: switch the feature grid to its
   * stacked/mobile composition before the rest of the page becomes mobile. */
  .tunnel-solution__feature-grid {
    gap: clamp(8px, 1.6vw, var(--pixso-gap-sm));
  }

  .tunnel-solution__feature-top-row,
  .tunnel-solution__feature-bottom-row {
    grid-template-columns: 1fr;
    gap: clamp(8px, 1.6vw, var(--pixso-gap-sm));
  }

  .tunnel-solution__feature-right-stack,
  .tunnel-solution__feature-column,
  .tunnel-solution__feature-mini-row,
  .tunnel-solution__feature-bottom-pair {
    gap: clamp(8px, 1.6vw, var(--pixso-gap-sm));
  }

  .tunnel-solution__feature-right-stack {
    grid-template-rows: auto auto;
  }

  .tunnel-solution__feature-bottom-pair--left {
    grid-template-columns: minmax(0, 320fr) minmax(0, 540fr);
  }

  .tunnel-solution__feature-bottom-pair--right {
    grid-template-columns: minmax(0, 540fr) minmax(0, 320fr);
  }
}

@media (max-width: 768px) {
  .tunnel-solution {
    --tunnel-page-bottom-space: 96px;
  }

  .tunnel-solution__hero {
    height: var(--pixso-hero-mobile);
    min-height: var(--pixso-hero-mobile);
  }

  .tunnel-solution__hero-copy {
    top: 76px;
    width: calc(100% - 32px);
  }

  .tunnel-solution__hero-kicker {
    margin-top: 4px;
  }

  .tunnel-solution__section-head {
    gap: var(--pixso-gap-sm);
  }

  .tunnel-solution__feature-mini-row {
    min-height: clamp(156px, 28vw, 240px);
  }

  .tunnel-solution__how {
    height: 100svh;
    min-height: 100svh;
    max-height: 100svh;
  }

  .tunnel-solution__how-stage {
    gap: clamp(12px, 2.5vh, 32px);
    padding-block: clamp(56px, 7vh, 88px) clamp(10px, 2vh, 28px);
  }

  .tunnel-solution__how-card-shell {
    --tunnel-how-video-max-height: min(44vh, 420px, calc(100svh - 20rem));
    max-width: min(
      calc(100% - var(--pixso-page-padding)),
      960px,
      calc(var(--tunnel-how-video-max-height) * 16 / 9)
    );
  }

  .tunnel-solution__feature-card--copy {
    padding: clamp(10px, 2.8vw, 18px);
  }

  .tunnel-solution__feature-card--copy > div {
    /* Match 巡1 icon clamp max (44px) + gap. */
    padding-bottom: calc(var(--pixso-gap-xs) + 44px);
  }

  .tunnel-solution__feature-hero-copy {
    left: clamp(18px, 5vw, 40px);
    right: clamp(18px, 5vw, 40px);
    top: clamp(14px, 3.2vw, var(--pixso-gap-md));
  }

  .tunnel-solution__feature-hero-copy h3 {
    font-size: var(--pixso-fs-32);
    line-height: var(--pixso-lh-32);
  }

  .tunnel-solution__feature-card--copy h3 {
    font-size: var(--pixso-fs-18);
    line-height: var(--pixso-lh-18);
  }

  .tunnel-solution__feature-hero-copy p {
    font-size: var(--pixso-fs-18);
    line-height: var(--pixso-lh-18);
  }

  .tunnel-solution__feature-card--copy p {
    font-size: var(--pixso-fs-12);
    line-height: var(--pixso-lh-12);
  }

  .tunnel-solution__metric-copy {
    left: clamp(14px, 4.2vw, 34px);
    right: clamp(14px, 3.2vw, var(--pixso-gap-md));
    top: clamp(28px, 7.5vw, 52px);
  }

  .tunnel-solution__feature-card--duration .tunnel-solution__metric-copy {
    top: clamp(36px, 9vw, 60px);
  }

  .tunnel-solution__feature-card--duration .tunnel-solution__metric-copy strong {
    font-size: var(--pixso-fs-48);
    line-height: var(--pixso-lh-48);
  }

  .tunnel-solution__feature-card--duration .tunnel-solution__metric-copy span {
    font-size: var(--pixso-fs-16);
    line-height: var(--pixso-lh-16);
  }

  .tunnel-solution__feature-card--tether .tunnel-solution__metric-copy {
    left: clamp(14px, 4.2vw, 34px);
  }

  .tunnel-solution__metric-copy strong {
    font-size: var(--pixso-fs-56);
    line-height: var(--pixso-lh-56);
  }

  .tunnel-solution__feature-card--image-wide .tunnel-solution__metric-copy strong {
    font-size: var(--pixso-fs-48);
    line-height: var(--pixso-lh-48);
  }

  .tunnel-solution__feature-card--temperature .tunnel-solution__metric-copy strong {
    font-size: var(--pixso-fs-36);
    line-height: var(--pixso-lh-36);
  }

  .tunnel-solution__feature-card--stairs .tunnel-solution__metric-copy {
    left: clamp(14px, 4vw, 30px);
    max-width: 52%;
  }

  .tunnel-solution__metric-copy span {
    font-size: var(--pixso-fs-18);
    line-height: var(--pixso-lh-18);
  }

  .tunnel-solution__feature-size {
    max-width: calc(100% - var(--pixso-gap-md) * 2);
  }

  .tunnel-solution__feature-size strong {
    font-size: var(--pixso-fs-20);
    line-height: var(--pixso-lh-20);
  }

  .tunnel-solution__feature-size span {
    font-size: var(--pixso-fs-16);
    line-height: var(--pixso-lh-16);
  }

  .tunnel-solution__feature-icon-image,
  .tunnel-solution__battery-icon {
    right: clamp(10px, 1.8vw, 24px);
    bottom: clamp(10px, 1.8vw, 24px);
    /* Align with FeatureMosaicCard @media (max-width: 1200px) icon scale. */
    width: clamp(22px, 14cqw, 44px);
    height: clamp(22px, 14cqw, 44px);
  }

  .tunnel-solution__battery-icon {
    width: clamp(22px, 14cqw, 44px);
    height: clamp(22px, 14cqw, 44px);
  }

  .tunnel-solution__feature-card--duration .tunnel-solution__battery-icon {
    right: clamp(10px, 1.8vw, 24px);
    bottom: clamp(10px, 1.8vw, 24px);
  }
}

@media (max-width: 480px) {
  .tunnel-solution--en .tunnel-solution__feature-grid {
    --tunnel-mini-card-min-height: clamp(156px, calc(428px - 60vw), 212px);
  }

  .tunnel-solution--en .tunnel-solution__feature-mini-row .tunnel-solution__feature-card {
    width: 100%;
    max-width: 100%;
    min-height: var(--tunnel-mini-card-min-height);
  }

  .tunnel-solution--en .tunnel-solution__feature-card--copy {
    display: block;
    padding: var(--pixso-gap-sm);
  }

  .tunnel-solution--en .tunnel-solution__feature-card--copy > div {
    position: relative;
    z-index: 2;
    max-width: 100%;
    padding-bottom: calc(var(--pixso-gap-xs) + 44px);
  }

  .tunnel-solution--en .tunnel-solution__feature-mini-row .tunnel-solution__feature-card h3 {
    font-size: var(--pixso-fs-14);
    line-height: var(--pixso-lh-14);
    white-space: normal;
    word-break: normal;
    overflow-wrap: normal;
  }

  .tunnel-solution--en .tunnel-solution__feature-mini-row .tunnel-solution__feature-card p {
    font-size: var(--pixso-fs-10);
    line-height: var(--pixso-lh-10);
    white-space: normal;
    word-break: normal;
    overflow-wrap: normal;
  }

  .tunnel-solution--en .tunnel-solution__feature-mini-row .tunnel-solution__feature-icon-image {
    right: var(--pixso-gap-sm);
    bottom: var(--pixso-gap-sm);
    /* Keep 巡1 responsive icon size; do not pin to 28px. */
    width: clamp(22px, 14cqw, 44px);
    height: clamp(22px, 14cqw, 44px);
  }
}
</style>
